import React, { useMemo } from "react";
import classnames from "classnames";

import { Dispensary } from "@/custom-types/Dispensary";
import {
  formatSchedule,
  getShortenedTimeZone,
  getTodayWeekday,
} from "@/utils/dispensaryScheduleUtils";

const RetailHours: React.FC<{
  className?: string;
  headerAs?: React.ElementType;
  schedules: Dispensary["schedules"] | null;
  timeZone: Dispensary["timeZone"];
}> = ({ className, headerAs: Header = "h3", schedules, timeZone }) => {
  const shortenedTimeZone = useMemo(
    () => timeZone && getShortenedTimeZone(timeZone),
    [timeZone],
  );
  const schedule = useMemo(
    () => schedules && formatSchedule(schedules, "store", { todayFirst: true }),
    [JSON.stringify(schedules)],
  );

  return (
    <div className={className}>
      <Header className="text-xs mb-sm uppercase">
        <span>Hours and Info</span>
        {shortenedTimeZone && <span>&nbsp;({shortenedTimeZone})</span>}
      </Header>
      {!schedule?.length ? (
        <div className="font-bold p-xs text-green">Hours unavailable</div>
      ) : (
        <div className="text-xs flex flex-col gap-1">
          {schedule.map((dailySchedule) => {
            const { day, open, close, isOpen24Hours, isSpecialHours } =
              dailySchedule || {};
            const isToday = getTodayWeekday() === day;
            const dailyHours = isOpen24Hours
              ? "Open 24 hours"
              : open?.time && close?.time
                ? `${open.time} - ${close.time}`
                : "Closed";

            return (
              <div
                key={day}
                className={classnames("flex justify-between", {
                  "font-bold": isToday,
                  "text-grey": !isToday,
                })}
              >
                <div className="w-1/2">
                  <div className="capitalize">{day}</div>
                  {isSpecialHours && <div>(Special hours)</div>}
                </div>
                <div data-testid={`${day}-hours`} className="w-1/2">
                  {dailyHours}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RetailHours;
